import React, { useEffect, useState } from 'react';
import { PencilLine, Save } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import HelpTip from '../../../components/HelpTip';

const ROWS = [
  ['registered', 'Job seekers registered', 'kpi_registered'],
  ['vacancies', 'Vacancies solicited', 'kpi_vacancies'],
  ['referred', 'Referred to employers', 'kpi_referred'],
  ['placed', 'Placed (hired)', 'kpi_placed'],
  ['career', 'Career guidance & counseling', 'career'],
  ['job_fairs', 'Job fairs held', 'prog_jobfair'],
];

/**
 * Enter this month's real numbers: the platform-counted floor (read-only) plus off-platform
 * / walk-in work you type. Saving writes SPRS overrides — the same numbers as your DOLE SPRS
 * report. Also marks whether this month's LMI report was filed on time.
 */
export default function MonthEntry({ monthly, monthLabel, saving, onSaveNumbers, lmiThisMonth, onSetLmi }) {
  const floor = monthly?.floor || {};
  const composed = monthly?.composed || {};
  const floorKey = JSON.stringify(floor);
  const composedKey = JSON.stringify(composed);
  // off-platform additions above the platform floor
  const [manual, setManual] = useState({});
  useEffect(() => {
    const next = {};
    ROWS.forEach(([k]) => { next[k] = Math.max(0, (composed[k] || 0) - (floor[k] || 0)); });
    setManual(next);
  }, [floorKey, composedKey]);

  const [savedFlash, setSavedFlash] = useState(false);

  const setOff = (k, v) => setManual((m) => ({ ...m, [k]: Math.max(0, parseInt(v, 10) || 0) }));

  const save = async () => {
    const numbers = {};
    ROWS.forEach(([k]) => { numbers[k] = (floor[k] || 0) + (manual[k] || 0); });
    const ok = await onSaveNumbers(numbers);
    if (ok) { setSavedFlash(true); setTimeout(() => setSavedFlash(false), 1400); }
  };

  const lmiBtn = (val, label, onCls) => (
    <button
      type="button"
      onClick={() => onSetLmi(val)}
      className={`text-xs font-semibold px-2.5 py-1 rounded-md border ${lmiThisMonth === val ? onCls : 'bg-white text-slate-600 border-slate-300'}`}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-xl border border-blue-200 bg-white p-4">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-slate-900 flex items-center gap-1.5 flex-wrap">
          <PencilLine className="h-4 w-4 text-blue-700" /> Enter {monthLabel}’s numbers <HelpTip id="entry" />
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">PLATFORM COUNTS + YOU ADD</span>
        </h3>
        <Button size="sm" onClick={save} disabled={saving} className={savedFlash ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-blue-700 hover:bg-blue-800'}>
          <Save size={14} className="mr-1" /> {savedFlash ? 'Saved ✓' : (saving ? 'Saving…' : 'Save this month')}
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Add anything the platform didn’t see — walk-ins, or vacancies an employer posted off-platform (for example on Facebook).
        These are the same numbers as your DOLE SPRS report.
        <span className="block mt-1">
          For <b>career guidance &amp; counseling</b>, “Platform counted” is how many people you ticked <b>Counseling</b> for on
          Form R1 (Applicants Registered) — add the students and parents you reached in career-guidance talks.
        </span>
      </p>
      <div>
        {ROWS.map(([k, label, help]) => {
          const p = floor[k] || 0;
          const total = p + (manual[k] || 0);
          return (
            <div key={k} className="flex flex-wrap items-center gap-x-4 gap-y-1 border-b border-slate-100 py-2">
              <span className="font-medium text-slate-700 text-sm w-full sm:w-52 flex items-center gap-1">{label} <HelpTip id={help} /></span>
              <span className="text-xs text-slate-500">Platform counted: <b className="text-slate-700 tabular-nums">{p}</b></span>
              <label className="text-xs text-slate-500 flex items-center gap-1.5">
                + off-platform / walk-in
                <input
                  type="number" min="0" value={manual[k] ?? 0}
                  onChange={(e) => setOff(k, e.target.value)}
                  className="w-16 h-8 border border-slate-300 rounded px-2 text-sm tabular-nums"
                />
              </label>
              <span className="text-xs text-slate-500">= <b className="text-slate-900 tabular-nums text-sm">{total}</b> total</span>
            </div>
          );
        })}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 py-2">
          <span className="font-medium text-slate-700 text-sm w-full sm:w-52 flex items-center gap-1">DOLE report filed on time? <HelpTip id="kpi_lmi" /></span>
          <div className="flex gap-1.5">
            {lmiBtn(true, 'Yes, on time', 'bg-emerald-500 text-white border-emerald-500')}
            {lmiBtn(false, 'No / late', 'bg-rose-500 text-white border-rose-500')}
            {lmiBtn(null, 'Not due yet', 'bg-slate-200 text-slate-700 border-slate-300')}
          </div>
        </div>
      </div>
    </div>
  );
}
